// client/src/views/OfflineSidebar.js
import React, { useState, useEffect } from "react";

const TIP_BAR_HEIGHT = 68;

function OfflineSidebar({
  sidebarWidth,
  sidebarRef,
  logo,
  mapDisplayName,
  customCategories,
  customTypes,
  typeColours,
  visibleTypes,
  setVisibleTypes,
  visibleCategories,
  setVisibleCategories,
  expandedCategories,
  setExpandedCategories,
  expandedTypes,
  setExpandedTypes,
  toggleCategoryExpand,
  toggleCategory,
  toggleType,
  handleMouseDown,
  pois,
  zones = [],
  visibleZones,
  setVisibleZones,
  hoveredZoneId,
  setHoveredZoneId,
  tipUrl,
  poiLabelToggleBtn,
  isMobile = false,
  hoveredType,
  hoveredPoiId,
  setHoveredPoiId,
  handleTypeHover,
  handleTypeUnhover,
  setHideSidebarHover,
  styles = {},
}) {
  const [zonesExpanded, setZonesExpanded] = useState(true);

  const allTypeIds = Array.isArray(customTypes) ? customTypes.map(t => t.id) : [];
  const allTypesVisible = visibleTypes && allTypeIds.length > 0 && allTypeIds.every(id => visibleTypes.has(id));

  function toggleAllTypes() {
    if (allTypesVisible) {
      setVisibleTypes(new Set());
    } else {
      setVisibleTypes(new Set(allTypeIds));
    }
  }

  useEffect(() => {
    if (!customCategories || !setVisibleCategories) return;
    setVisibleCategories((prev) => {
      const next = new Set(prev);
      customCategories.forEach((cat) => {
        if (!next.has(cat.id)) {
          next.add(cat.id);
        }
      });
      return next;
    });
  }, [customCategories, setVisibleCategories]);

  const allZonesVisible =
    zones.length > 0 && zones.every((z) => visibleZones.has(z.id));

  const sidebarBg = (styles && styles.sidebar_bg_colour) || "#202124";
  const mainFont = (styles && styles.font_family) || "inherit";
  const fontColour = (styles && styles.font_colour) || "#fff";

  const sectionNamePOIs = (styles && styles.section_name_pois) || "POIs";
  const sectionNameZones = (styles && styles.section_name_zones) || "Zones";
  const creatorSignature = styles?.creator_signature || null;
  const watermarkUrl = styles?.watermark_url || null;

  const toggleZoneVisibility = (zoneId) => {
    setVisibleZones((prev) => {
      const next = new Set(prev);
      if (next.has(zoneId)) {
        next.delete(zoneId);
      } else {
        next.add(zoneId);
      }
      return new Set(next);
    });
  };

  const selectAllZones = () => {
    setVisibleZones(new Set(zones.map((z) => z.id)));
  };

  const deselectAllZones = () => {
    setVisibleZones(new Set());
  };

  const typesByCategoryId = {};
  (Array.isArray(customTypes) ? customTypes : []).forEach((type) => {
    if (typeof type.category_id !== "undefined" && type.category_id !== null) {
      if (!typesByCategoryId[type.category_id]) typesByCategoryId[type.category_id] = [];
      typesByCategoryId[type.category_id].push(type);
    }
  });

  const hasTitleImage = Boolean(styles?.title_image_url);

  return (
    <div
      className={`sidebar${hasTitleImage ? " has-title-image" : ""}`}
      style={{
        width: isMobile ? "100vw" : `${sidebarWidth}px`,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: isMobile ? "fixed" : "relative",
        top: 0,
        left: 0,
        background: sidebarBg,
        fontFamily: mainFont,
        color: fontColour,
        zIndex: 2000,
        ...(isMobile && {
          minWidth: 0,
          maxWidth: "100vw",
          padding: 0,
          borderRadius: 0,
          boxShadow: "none",
        }),
        overflow: "hidden",
      }}
      ref={sidebarRef}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: hasTitleImage ? 0 : 5,
          paddingTop: hasTitleImage ? 0 : 12,
          background: "transparent",
          width: "100%",
          paddingLeft: hasTitleImage ? 0 : 5,
          paddingRight: hasTitleImage ? 0 : 12,
          boxSizing: "border-box",
          userSelect: "none",
        }}
      >
        <a href="/" title="Go to Home" style={{ marginBottom: hasTitleImage ? -100 : -50 }}>
          <img
            src={logo}
            alt="Point Atlas Logo"
            style={{
              maxWidth: 50,
              maxHeight: 50,
              width: "auto",
              height: "auto",
              display: "block",
              cursor: "pointer",
              marginBottom: 50,
              marginTop: 20,
            }}
          />
        </a>

        {hasTitleImage && (
          <img
            src={styles.title_image_url}
            alt="Game Logo"
            style={{
              width: "80%",
              maxHeight: 150,
              objectFit: "fill",
              display: "block",
              marginTop: 0,
              marginBottom: 0,
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        )}

        {!hasTitleImage && (
          <span
            style={{
              fontWeight: "bold",
              fontSize: "1.90em",
              margin: "4px 0 0 0",
              letterSpacing: "1px",
              color: fontColour,
              textAlign: "center",
              width: "100%",
              fontFamily: mainFont,
              userSelect: "none",
            }}
          >
            {styles.title_text || mapDisplayName}
          </span>
        )}

        {creatorSignature && (
          <div
            style={{
              color: "#ccc",
              fontSize: 13,
              fontWeight: "bold",
              margin: "4px 0 0 0",
              fontStyle: "italic",
              textAlign: "center",
              width: "90%",
              letterSpacing: "0.5px",
              opacity: 0.82,
              userSelect: "none",
            }}
          >
            {creatorSignature}
          </div>
        )}
      </div>

      {poiLabelToggleBtn && (
        <div
          style={{
            textAlign: "center",
            margin: hasTitleImage ? "0 0 4px 0" : "12px 0 4px 0",
          }}
        >
          {poiLabelToggleBtn}
        </div>
      )}

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          background: "transparent",
          paddingBottom: `${TIP_BAR_HEIGHT + 8}px`,
        }}
      >
        <h3 style={{ color: fontColour, fontFamily: mainFont }}>{sectionNamePOIs}</h3>
        <button onClick={toggleAllTypes} style={{ marginBottom: "10px" }}>
          {allTypesVisible ? "Hide All" : "Show All"}
        </button>
        {(customCategories || []).map((cat) => (
          <div key={cat.id}>
            <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <span
                onClick={(e) => {
                  setExpandedCategories((prev) => ({
                    ...prev,
                    [cat.id]: !prev[cat.id],
                  }));
                  if (typeof toggleCategoryExpand === "function") toggleCategoryExpand(cat.id, e);
                }}
                style={{
                  marginRight: 5,
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 700,
                  fontSize: "1.2em",
                }}
                tabIndex={0}
                role="button"
                aria-label={expandedCategories[cat.id] ? "Collapse" : "Expand"}
              >
                {expandedCategories[cat.id] ? "▼" : "►"}
              </span>
              <input
                type="checkbox"
                checked={visibleCategories.has(cat.id)}
                onChange={() => toggleCategory(cat.id)}
                style={{ marginRight: 5 }}
              />
              <strong>{cat.name}</strong>
            </div>
            {expandedCategories[cat.id] && (
              <ul>
                {(typesByCategoryId[cat.id] || []).map((type) => {
                  const count = pois
                    ? pois.filter((p) => {
                        const poiType = customTypes.find((t) => t.id === p.type_id);
                        return poiType?.category_id === cat.id && p.type_id === type.id;
                      }).length
                    : 0;
                  const color =
                    typeof type.color === "string" && type.color[0] === "#"
                      ? type.color
                      : "#" + (type.color || "fff");
                  const poisOfType = pois
                    ? pois.filter((p) => {
                        const poiType = customTypes.find((t) => t.id === p.type_id);
                        return poiType?.category_id === cat.id && p.type_id === type.id;
                      })
                    : [];
                  return (
                    <li
                      key={type.id}
                      style={{
                        marginBottom: 3,
                        cursor: isMobile ? "pointer" : "default",
                      }}
                      onMouseEnter={() => {
                        if (typeof handleTypeHover === "function") handleTypeHover(type.id);
                        if (setHideSidebarHover) setHideSidebarHover(true);
                      }}
                      onMouseLeave={() => {
                        if (typeof handleTypeUnhover === "function") handleTypeUnhover();
                        if (setHideSidebarHover) setHideSidebarHover(false);
                      }}
                    >
                      <span
                        onClick={() => {
                          setExpandedTypes((prev) => ({
                            ...prev,
                            [type.id]: !prev[type.id],
                          }));
                        }}
                        style={{
                          cursor: "pointer",
                          marginRight: 6,
                          userSelect: "none",
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={
                          expandedTypes[type.id] ? "Collapse POIs" : "Expand POIs"
                        }
                      >
                        {expandedTypes[type.id] ? "▼" : "►"}
                      </span>
                      <input
                        type="checkbox"
                        checked={visibleTypes.has(type.id)}
                        onChange={() => toggleType(type.id)}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          backgroundColor: color,
                          marginLeft: "6px",
                          marginRight: "6px",
                          borderRadius: "2px",
                        }}
                      />
                      {type.name} ({count})
                      {expandedTypes[type.id] && poisOfType.length > 0 && (
                        <ul style={{ marginLeft: 24, marginTop: 2 }}>
                          {poisOfType.map((poi) => (
                            <li
                              key={poi.id}
                              className="sidebar-poi-item"
                              onMouseEnter={() => {
                                setHoveredPoiId && setHoveredPoiId(poi.id);
                                setHideSidebarHover && setHideSidebarHover(true);
                              }}
                              onMouseLeave={() => {
                                setHoveredPoiId && setHoveredPoiId(null);
                                setHideSidebarHover && setHideSidebarHover(false);
                              }}
                              onClick={() => setHoveredPoiId && setHoveredPoiId(poi.id)}
                            >
                              <div className="sidebar-poi-content">
                                <span
                                  className="sidebar-poi-icon"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="sidebar-poi-label">
                                  {poi.poi_name || "(unnamed)"}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
        {zones.length > 0 && (
          <div
            style={{
              marginTop: 30,
              paddingLeft: 8,
              borderTop: "1px solid #444",
              userSelect: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 0,
                marginTop: 16,
              }}
            >
              <div
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "1.3em",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                onClick={() => setZonesExpanded(!zonesExpanded)}
                aria-expanded={zonesExpanded}
                role="button"
                tabIndex={0}
              >
                {zonesExpanded ? "▼" : "►"} {sectionNameZones}
              </div>
            </div>
            <div style={{ margin: "8px 0 4px 0", display: "flex", gap: 8 }}>
              <button
                onClick={allZonesVisible ? deselectAllZones : selectAllZones}
                style={{
                  fontSize: 14,
                  padding: "2px 13px",
                  borderRadius: 4,
                  background: "#282931",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  marginRight: 4,
                }}
              >
                {allZonesVisible ? "Hide All" : "Show All"}
              </button>
              <button
                onClick={() => setZonesExpanded(!zonesExpanded)}
                title={zonesExpanded ? "Collapse Zones" : "Expand Zones"}
                style={{
                  fontSize: 18,
                  lineHeight: 1,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "#282931",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {zonesExpanded ? "−" : "+"}
              </button>
            </div>
            {zonesExpanded && (
              <ul style={{ paddingLeft: 12, marginTop: 0 }}>
                {zones.map((zone) => {
                  const label = zone.label?.trim() || "Untitled";
                  const checked = visibleZones.has(zone.id);
                  return (
                    <li
                      key={zone.id}
                      style={{ marginBottom: 6, display: "flex", alignItems: "center" }}
                      onMouseEnter={() => setHoveredZoneId && setHoveredZoneId(zone.id)}
                      onMouseLeave={() => setHoveredZoneId && setHoveredZoneId(null)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleZoneVisibility(zone.id)}
                        style={{ marginRight: 8 }}
                        id={`zone-vis-${zone.id}`}
                      />
                      <label
                        htmlFor={`zone-vis-${zone.id}`}
                        style={{ cursor: "pointer", flexGrow: 1, userSelect: "none" }}
                      >
                        {label}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          background: "linear-gradient(90deg, #222 90%, #444)",
          padding: "16px 0 12px 0",
          textAlign: "center",
          zIndex: 200,
          boxShadow: "0 -2px 14px #0009",
        }}
      >
        <div
          style={{
            display: "inline-block",
            background: "linear-gradient(90deg, #40dca5 50%, #23be88 100%)",
            color: "#002b1e",
            borderRadius: 12,
            fontWeight: 800,
            fontSize: 15.5,
            padding: "10px 30px",
            margin: 0,
            textDecoration: "none",
            boxShadow: "0 1px 14px #00ffbf55",
            letterSpacing: "0.9px",
            border: "2px solid #0a7755",
          }}
        >
          <div style={{ fontSize: 16.5 }}>Don’t lose your map</div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              marginTop: 2,
              opacity: 0.95,
            }}
          >
            Create a free Point Atlas account and upload this map to back it up,
            share it, and open it from any device.
          </div>
        </div>
      </div>

      {watermarkUrl && (
        <img
          src={watermarkUrl}
          alt="Map Watermark"
          style={{
            position: "absolute",
            bottom: 32 + TIP_BAR_HEIGHT,
            left: "50%",
            transform: "translateX(-50%)",
            width: "85%",
            maxWidth: 330,
            opacity: 0.10,
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
      )}

      {!isMobile && <div className="resizer" onMouseDown={handleMouseDown} />}
    </div>
  );
}

export default OfflineSidebar;
